import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, type FC, useState, type MouseEvent } from 'react';

import { type Camera } from '../../models/index.js';
import { type WithRequired } from '../../utils/index.js';

interface StreamCamerasMenuProps {
  setExpansionLocked: (value: boolean) => void;
  setCamera: (camera: WithRequired<Partial<Camera>, 'id'>) => void;
  displayedCameraName: string;
  cameras: Camera[];
}
const StreamCamerasMenu: FC<StreamCamerasMenuProps> = ({
  setExpansionLocked,
  setCamera,
  displayedCameraName,
  cameras,
}) => {
  const [cameraMenuAnchorEl, setCameraMenuAnchorEl] = useState<null | HTMLElement>(null);
  const cameraMenuOpen = Boolean(cameraMenuAnchorEl);

  const handleCameraMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setExpansionLocked(true);
    setCameraMenuAnchorEl(event.currentTarget);
  };
  const handleCameraMenuClose = (camera?: Camera) => {
    if (camera) {
      setCamera(camera);
    }
    setCameraMenuAnchorEl(null);
    setExpansionLocked(false);
  };

  const sortedCameras = useMemo(() => {
    return sortBy(cameras, ['name']);
  }, [cameras]);

  const hasMultipleCameras = (cameras?.length || 0) > 1;

  return (
    <>
      <Tooltip arrow color="primary" placement="bottom" title={'Click to change camera'}>
        <Button
          sx={{
            color: 'white',
            '&.Mui-disabled': {
              color: 'white',
            },
          }}
          disableElevation
          id="camera-menu-button"
          aria-controls={cameraMenuOpen ? 'camera-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={cameraMenuOpen ? 'true' : undefined}
          onClick={handleCameraMenuClick}
          {...{
            ...(hasMultipleCameras
              ? {
                  endIcon: <ExpandMoreIcon />,
                }
              : { disabled: true }),
          }}
        >
          {displayedCameraName}
        </Button>
      </Tooltip>
      <Menu
        id="camera-menu"
        anchorEl={cameraMenuAnchorEl}
        open={cameraMenuOpen}
        onClose={() => handleCameraMenuClose()}
        MenuListProps={{
          'aria-labelledby': 'camera-menu-button',
        }}
      >
        {sortedCameras?.map((camera) =>
          (
            <MenuItem key={`${camera.id}-${camera.name}`} sx={{justifyContent: 'space-between'}} selected={camera.name === displayedCameraName} onClick={() => handleCameraMenuClose(camera)}>
              {camera.name}
              {camera.lastActivity ? <Box sx={{ml: 2, borderRadius: '4px', backgroundColor: 'lightgreen', color: 'black', p: 0.5, fontSize: '0.8rem'}}>{Math.floor((DateTime.fromISO(camera.lastActivity).diffNow().toMillis() / 1000 / 60) * -1)}m</Box> : null}
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
};

export default StreamCamerasMenu;
